import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, RefreshCw, Upload, Sparkles, Grid2x2, Link2 } from "lucide-react";
import { CtaButton, MonoLabel, Tag, SecondaryButton } from "../components/kit";
import TemplateRenderer from "../components/landing/TemplateRenderer";
import LandingEditorV2 from "./LandingEditorV2";
import api, { formatApiErrorDetail } from "../lib/api";

const SECTION_LABELS = {
  hero: "Hero", benefits: "Benefits", steps: "How it works", testimonials: "Testimonials", faq: "FAQ",
  final: "Final CTA", form: "Form", highlights: "Highlights", gallery: "Gallery", hours: "Hours & map",
  reviews: "Reviews", proof: "Social proof", guarantee: "Guarantee", next: "What happens next",
};
const REGEN_SECTIONS = {
  lead: ["hero", "benefits", "steps", "faq", "final", "form"],
  booking: ["hero", "highlights", "hours", "form"],
  offer: ["hero", "benefits", "guarantee", "faq", "final"],
  waitlist: ["hero", "benefits", "next", "form"],
};
const TEMPLATE_NAMES = { lead: "Lead Capture", booking: "Booking", offer: "Offer / Product", waitlist: "Waitlist" };

const setPath = (obj, path, value) => {
  const clone = JSON.parse(JSON.stringify(obj));
  const parts = path.split(".");
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
  cur[parts[parts.length - 1]] = value;
  return clone;
};

export default function LandingEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [landing, setLanding] = useState(undefined);
  const [tokens, setTokens] = useState({});
  const [regenBusy, setRegenBusy] = useState(null);
  const [pixel, setPixel] = useState("");

  useEffect(() => {
    api.get(`/landing/${id}`).then(({ data }) => { setLanding(data); setPixel(data.pixel_id || ""); }).catch(() => setLanding(null));
    api.get("/brand-brain").then(({ data }) => {
      const kit = data?.brand_kit || {};
      setTokens({ primary: kit.primary_color, secondary: kit.secondary_color, typography: kit.typography, has_logo: kit.has_logo, business_name: data?.business_name });
    }).catch(() => {});
  }, [id]);

  const patch = async (updates) => {
    const { data } = await api.patch(`/landing/${id}`, updates);
    setLanding(data);
    return data;
  };

  const onEdit = (path, value) => {
    const content = setPath(landing.content, path, value);
    setLanding({ ...landing, content });
    patch({ content }).catch(() => toast.error("Save failed — retry."));
  };
  const onArrayAdd = (key, item) => {
    const content = { ...landing.content, [key]: [...(landing.content[key] || []), item] };
    setLanding({ ...landing, content });
    patch({ content }).catch(() => {});
  };
  const onArrayRemove = (key, idx) => {
    const content = { ...landing.content, [key]: landing.content[key].filter((_, i) => i !== idx) };
    setLanding({ ...landing, content });
    patch({ content }).catch(() => {});
  };

  const toggleSection = (s) => {
    const sections = { ...landing.sections, [s]: landing.sections[s] === false };
    setLanding({ ...landing, sections });
    patch({ sections }).catch(() => {});
  };

  const regenerate = async (section) => {
    setRegenBusy(section);
    try {
      const { data } = await api.post(`/landing/${id}/regenerate`, { section });
      setLanding((l) => ({ ...l, content: data.content }));
      toast("Section rewritten.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRegenBusy(null);
    }
  };

  const switchTemplate = async (t) => {
    if (t === landing.template) return;
    if (!window.confirm(`Switch to "${TEMPLATE_NAMES[t]}"? The copy will be regenerated for the new template (your images, pixel and testimonials carry over).`)) return;
    setRegenBusy("switch");
    try {
      const { data } = await api.post(`/landing/${id}/switch-template`, { template: t });
      setLanding(data);
      toast("Template switched.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRegenBusy(null);
    }
  };

  const uploadImage = async (file, target) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/landing/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const images = target === "hero"
        ? { ...landing.images, hero: { mode: "upload", url: data.url } }
        : { ...landing.images, gallery: [...(landing.images.gallery || []), data.url] };
      await patch({ images });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const aiHeroImage = async () => {
    setRegenBusy("ai-image");
    try {
      const { data } = await api.post(`/landing/${id}/ai-image`);
      await patch({ images: { ...landing.images, hero: { mode: "ai", url: data.url } } });
      toast("AI scene applied.", { description: "This counted toward your monthly creative budget." });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRegenBusy(null);
    }
  };

  const savePixel = async () => {
    try {
      await patch({ pixel_id: pixel });
      toast(pixel ? "Pixel saved — PageView + Lead events will fire." : "Pixel removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const togglePublish = async () => {
    try {
      if (landing.status === "published") { await api.post(`/landing/${id}/unpublish`); setLanding({ ...landing, status: "draft" }); }
      else { await api.post(`/landing/${id}/publish`); setLanding({ ...landing, status: "published" }); toast("Published.", { description: `/l/${landing.slug} is live.` }); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (landing === undefined) return <div className="p-10"><div className="card-soft h-40 animate-pulse" /></div>;
  if (landing === null) return <div className="p-10" data-testid="editor-not-found">Landing not found.</div>;
  if (landing.engine === "v2") return <LandingEditorV2 id={id} />;

  const hasGallery = ["booking", "offer"].includes(landing.template);
  const logoUrl = tokens.has_logo ? `${process.env.REACT_APP_BACKEND_URL}/api/brand-kit/logo` : null;

  return (
    <div className="p-6 sm:p-8" data-testid="landing-editor-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => navigate("/app/performance/landing")} className="flex items-center gap-1.5 text-[13px] font-bold text-[#6E6D68] hover:text-[#1D1E26]" data-testid="editor-back-btn">
          <ArrowLeft size={14} /> Landings
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <Tag color={landing.status === "published" ? "green" : "neutral"}>{landing.status.toUpperCase()}</Tag>
          {landing.status === "published" && (
            <a href={`/l/${landing.slug}`} target="_blank" rel="noreferrer" className="text-[12.5px] font-bold underline text-[#6E6D68] hover:text-[#1D1E26]" data-testid="editor-view-live">/l/{landing.slug}</a>
          )}
          <CtaButton className="!py-2 !px-4 text-[13px]" onClick={togglePublish} data-testid="editor-publish-btn">
            {landing.status === "published" ? "Unpublish" : "Publish"}
          </CtaButton>
        </div>
      </div>

      <div className="card-soft mt-4 p-5" data-testid="editor-controls">
        <div className="flex flex-wrap gap-x-8 gap-y-4">
          <div>
            <MonoLabel>SECTIONS</MonoLabel>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.keys(landing.sections).map((s) => {
                const onS = landing.sections[s] !== false;
                const canRegen = REGEN_SECTIONS[landing.template].includes(s);
                return (
                  <span key={s} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11.5px] font-bold ${onS ? "bg-white border-[#E4E1DA] text-[#1D1E26]" : "bg-[#F1EFEA] border-[#F1EFEA] text-[#A3A19B]"}`}>
                    <button onClick={() => s !== "hero" && toggleSection(s)} title={onS ? "Hide section" : "Show section"} data-testid={`editor-toggle-${s}`}>
                      {onS ? <Eye size={12} strokeWidth={2} /> : <EyeOff size={12} strokeWidth={2} />}
                    </button>
                    {SECTION_LABELS[s] || s}
                    {canRegen && (
                      <button onClick={() => regenerate(s)} disabled={!!regenBusy} title="Regenerate copy" data-testid={`editor-regen-${s}`}>
                        <RefreshCw size={11} strokeWidth={2} className={regenBusy === s ? "animate-spin" : ""} />
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
          <div>
            <MonoLabel>HERO IMAGE</MonoLabel>
            <div className="flex flex-wrap gap-2 mt-2">
              <SecondaryButton className="!py-1.5 !px-3 !text-[11.5px]" onClick={() => patch({ images: { ...landing.images, hero: { mode: "pattern", url: null } } })} data-testid="editor-hero-pattern"><Grid2x2 size={11} /> Brand pattern</SecondaryButton>
              <label className="btn-secondary !py-1.5 !px-3 !text-[11.5px] cursor-pointer flex items-center gap-1"><Upload size={11} /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], "hero")} data-testid="editor-hero-upload" />
              </label>
              <SecondaryButton className="!py-1.5 !px-3 !text-[11.5px]" onClick={aiHeroImage} disabled={regenBusy === "ai-image"} data-testid="editor-hero-ai">
                <Sparkles size={11} /> {regenBusy === "ai-image" ? "Shooting…" : "AI scene (uses creative budget)"}
              </SecondaryButton>
            </div>
            {hasGallery && (
              <label className="btn-secondary !py-1.5 !px-3 !text-[11.5px] cursor-pointer inline-flex items-center gap-1 mt-2"><Upload size={11} /> Add gallery photo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], "gallery")} data-testid="editor-gallery-upload" />
              </label>
            )}
          </div>
          <div>
            <MonoLabel>META PIXEL</MonoLabel>
            <div className="flex items-center gap-2 mt-2">
              <input className="field-input !py-1.5 !text-[12.5px] w-[160px]" placeholder="Pixel ID (numbers)" value={pixel} onChange={(e) => setPixel(e.target.value)} data-testid="editor-pixel-input" />
              <SecondaryButton className="!py-1.5 !px-3 !text-[11.5px]" onClick={savePixel} data-testid="editor-pixel-save">Save</SecondaryButton>
            </div>
          </div>
          <div>
            <MonoLabel>TEMPLATE</MonoLabel>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(TEMPLATE_NAMES).map(([k, name]) => (
                <button key={k} onClick={() => switchTemplate(k)} disabled={!!regenBusy} className={`rounded-full px-3 py-1.5 text-[11.5px] font-bold border ${landing.template === k ? "bg-[#1D1E26] text-white border-[#1D1E26]" : "bg-white text-[#6E6D68] border-[#E4E1DA] hover:border-[#1D1E26]"}`} data-testid={`editor-template-${k}`}>
                  {name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="text-[11px] text-[#A3A19B] mt-4 flex items-center gap-1"><Link2 size={10} /> Click any text in the preview to edit it inline. Layout is locked by design.</p>
      </div>

      <div className="mt-5 rounded-2xl overflow-hidden border border-[#E4E1DA] shadow-[0_4px_32px_rgba(29,30,38,0.08)]" data-testid="editor-preview">
        <TemplateRenderer
          landing={landing}
          tokens={tokens}
          logoUrl={logoUrl}
          editable
          onEdit={onEdit}
          onArrayAdd={onArrayAdd}
          onArrayRemove={onArrayRemove}
        />
      </div>
    </div>
  );
}
